/* /assets/instant-search.js
   GG Instant Search — client-side catalog search for sections/instant-search.liquid.
   Requires assets/minisearch.min.js (MiniSearch UMD global) loaded first.

   Feeds:
   - products + pages: /search?view=search-data&q=*&type=product,page&page=N
     (templates/search.search-data.liquid)
   - collections: /collections?view=search-data&page=N
     (templates/list-collections.search-data.liquid)
   Cached in localStorage per currency/market.

   Badges replicate snippets/product-status-badges.liquid (same classes, so
   theme CSS and admin badge settings apply): promotion wins outright; sold out
   stacks with status badges; custom metafield badge beats sale. */
(function () {
  'use strict';

  if (customElements.get('gg-instant-search')) return;

  var CACHE_PREFIX = 'gg-search-index:v8:'; // v8: catalog past the 1,000-result search cap
  var CACHE_TTL = 24 * 60 * 60 * 1000; // hard expiry — older caches are ignored entirely
  var FRESH_TTL = 5 * 60 * 1000;       // soft expiry default — overridable via section setting (freshMinutes)
  var MAX_PAGES = 20;

  function norm(s) {
    return (s || '').toString().toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  }

  function tokenize(s) {
    return norm(s).split(/[^a-z0-9]+/).filter(Boolean);
  }

  function joinedPairs(tokens) {
    var out = [];
    for (var i = 0; i < tokens.length - 1; i++) out.push(tokens[i] + tokens[i + 1]);
    return out;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function imgSrc(u) {
    if (!u || typeof u !== 'string') return '';
    return u.indexOf('//') === 0 ? 'https:' + u : u;
  }

  function indexOfSeq(haystack, needle) {
    if (!needle.length) return -1;
    outer: for (var i = 0; i <= haystack.length - needle.length; i++) {
      for (var j = 0; j < needle.length; j++) {
        if (haystack[i + j] !== needle[j]) continue outer;
      }
      return i;
    }
    return -1;
  }

  /* "Buyable" means in stock AND actually purchasable — the hide-button tag
     marks display-only products whose buy button the theme suppresses. */
  function isBuyable(p) {
    return !!p.available && (p.tags || []).indexOf('hide-button') === -1;
  }

  /* Pre-orders are classified by the "Preorder" tag OR the product_status
     metafield — the catalog maintains both inconsistently (546 tagged vs 22
     with status). Active = still buyable; past = ended or sold through.
     Memoized on the product object: called per product per filter pass. */
  function availabilityOf(p) {
    if (p._av) return p._av;
    var tagged = (p.tags || []).some(function (t) { return /^pre-?order$/i.test(t.trim()); });
    var pre = tagged || p.status === 'pre_order' || p.status === 'pre_order_ended';
    if (pre) {
      p._av = isBuyable(p) && p.status !== 'pre_order_ended' ? 'Active pre-order' : 'Past pre-order';
    } else if (p.status === 'coming_soon') {
      p._av = 'Coming soon';
    } else {
      p._av = isBuyable(p) ? 'In stock' : 'Sold out';
    }
    return p._av;
  }

  /* Supp type comes from tags — the product.type field is unmaintained
     ("Other" would be 80% of the catalog). Multi-label like flavors. */
  function suppTypesOf(p) {
    if (p._st) return p._st;
    var tags = new Set((p.tags || []).map(function (t) { return norm(t).trim(); }));
    var out = [];
    if (tags.has('energy')) out.push('Energy');
    if (tags.has('caffeine-free')) out.push('Caffeine Free');
    // Sleep line splits on melatonin — the buckets are disjoint on purpose
    if (tags.has('melatonin')) out.push('Sleep Supplement + Melatonin');
    else if (tags.has('afk')) out.push('Sleep Supplement');
    // GAMERAID products carry no line tag yet — title match until they do
    if (tags.has('gameraid') || /gameraid/i.test(p.title || '')) out.push('Creatine Supplement');
    p._st = out;
    return out;
  }

  /* Metafield flavor values are inconsistent ("Cream ", "cream") — facet on a
     normalized key, display title-cased. Memoized on the product object. */
  function flavorKeys(p) {
    if (p._fk) return p._fk;
    p._fk = (p.flavors || []).map(function (f) {
      return norm(f).replace(/\s+/g, ' ').trim();
    }).filter(Boolean);
    return p._fk;
  }

  function titleCase(s) {
    return s.replace(/(^|\s)[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  // Purpose-drawn at 16px — the theme's 192-viewBox header icon goes blurry
  // this small and drags its cart-count indicator dot along.
  var CART_ICON = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M1.5 2h1.7l1.75 8.3a1.2 1.2 0 0 0 1.17.95h6.02a1.2 1.2 0 0 0 1.17-.93L14.5 5.2H4.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><circle cx="6.4" cy="13.3" r="1.15" fill="currentColor"/><circle cx="11.7" cy="13.3" r="1.15" fill="currentColor"/></svg>';

  var ARROW_PREV = '<button type="button" class="gg-aux__arrow" data-gg-rail-prev aria-label="Scroll left" hidden><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M7.5 2L3.5 6l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
  var ARROW_NEXT = '<button type="button" class="gg-aux__arrow" data-gg-rail-next aria-label="Scroll right" hidden><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M4.5 2l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';

  function parseSynonyms(raw) {
    // One group per line, comma-separated terms: "mousepad, mouse pad, desk mat"
    return String(raw || '')
      .split(/\r?\n/)
      .map(function (line) {
        return line.split(',').map(function (t) { return norm(t.trim()); }).filter(Boolean);
      })
      .filter(function (group) { return group.length > 1; });
  }

  function buildMiniIndex(docs, fields, boost) {
    var ms = new MiniSearch({
      fields: fields,
      searchOptions: {
        boost: boost,
        prefix: true,
        fuzzy: 0.2
      },
      extractField: function (doc, field) {
        if (field === 'joined') return joinedPairs(tokenize(doc.title)).join(' ');
        if (field === 'tags') return (doc.tags || []).join(' ');
        if (field === 'flavors') return (doc.flavors || []).join(' ');
        return doc[field];
      }
    });
    if (docs.length) ms.addAll(docs);
    return ms;
  }

  class InstantSearchElement extends HTMLElement {
    connectedCallback() {
      // Re-initialize only after a real teardown — the guard also absorbs
      // same-tick detach/reattach moves that morphing renderers perform.
      if (this._initialized && !this._destroyed) return;
      this._initialized = true;
      this._destroyed = false;

      this.products = [];
      this.pages = [];
      this.collections = [];
      this.byId = new Map();
      this.ms = null;
      this.msCollections = null;
      this.msPages = null;
      this.partial = true;

      // Infinite scroll: auto-expand when the sentinel nears the viewport.
      var self = this;
      this.io = 'IntersectionObserver' in window
        ? new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) self.expand();
            });
          }, { rootMargin: '600px 0px' })
        : null;

      this.syncRefs();

      // Only load + swap hover images on devices that can actually hover
      this.hoverCapable = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

      this.state = {
        q: '',
        supp: new Set(),
        merch: new Set(),
        avail: new Set(),
        flavor: new Set(),
        collection: null, // { handle, label, ids: Set } — featured-tab quick filter
        priceMin: null,
        priceMax: null,
        sort: 'newest', // browse default; auto-switches to relevance while querying
        visible: this.perPage
      };
      this._sortExplicit = false;
      this.readStateFromUrl();
      this.restoreControls();

      this.bindEvents();
      this.watchDom();

      // Returning to a tab left open (e.g. across a drop) revalidates the
      // index. Document-level listener: MUST be removed in teardown() or every
      // replaced instance stays reachable forever (catalog + index and all).
      this._onVisibility = function () {
        if (self._destroyed || !self.isConnected) return;
        if (document.visibilityState === 'visible' && self.ms
          && Date.now() - (self.lastLoadTs || 0) > self.freshTtl) {
          self.refresh();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      this.renderSkeleton();
      this.init();
    }

    /* Section re-renders in the theme editor can replace this node outright.
       Defer one tick so a detach-and-reattach move doesn't tear down a live
       element; a genuinely removed one must release its observers, document
       listener, and catalog/index so it can be garbage collected. */
    disconnectedCallback() {
      var self = this;
      setTimeout(function () {
        if (!self.isConnected) self.teardown();
      }, 0);
    }

    teardown() {
      if (this._destroyed) return;
      this._destroyed = true;
      this.stopLoader();
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._mo) this._mo.disconnect();
      if (this.io) this.io.disconnect();
      this.ms = null;
      this.msCollections = null;
      this.msPages = null;
      this.products = [];
      this.pages = [];
      this.collections = [];
      this.byId = new Map();
      this.currentBase = null;
      this.currentResults = null;
    }

    /* Resolve child references and config. Called on boot and again whenever
       the section's DOM is re-rendered under us (theme editor setting changes,
       theme dev hot reload) — the host element survives those swaps but every
       child node is replaced. */
    syncRefs() {
      this.form = this.querySelector('[data-gg-form]');
      this.input = this.querySelector('[data-gg-input]');
      this.clearBtn = this.querySelector('[data-gg-clear]');
      this.countEl = this.querySelector('[data-gg-count]');
      this.sortEl = this.querySelector('[data-gg-sort]');
      this.filtersEl = this.querySelector('[data-gg-filters]');
      this.priceMinEl = this.querySelector('[data-gg-price-min]');
      this.priceMaxEl = this.querySelector('[data-gg-price-max]');
      this.collectionsEl = this.querySelector('[data-gg-collections]');
      this.pagesEl = this.querySelector('[data-gg-pages]');
      this.resultsEl = this.querySelector('[data-gg-results]');
      this.moreBtn = this.querySelector('[data-gg-more]');
      this.emptyEl = this.querySelector('[data-gg-empty]');
      this.errorEl = this.querySelector('[data-gg-error]');
      this.sentinel = this.querySelector('[data-gg-sentinel]');
      this.featuredEl = this.querySelector('[data-gg-featured]');
      this.filtersBtn = this.querySelector('[data-gg-filters-open]');
      this.filtersBadge = this.querySelector('[data-gg-filters-badge]');
      this.modal = this.querySelector('[data-gg-filters-modal]');
      this.modalCount = this.querySelector('[data-gg-modal-count]');
      this.appliedEl = this.querySelector('[data-gg-applied]');
      this.qvModal = this.querySelector('[data-gg-qv-modal]');
      this.qvBody = this.querySelector('[data-gg-qv-body]');

      var cfgEl = this.querySelector('[data-gg-config]');
      var cfg = {};
      try { cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { cfg = {}; }

      this.endpoint = this.dataset.endpoint || '/search';
      this.collectionsEndpoint = this.dataset.collectionsEndpoint || '/collections';
      this.cacheKey = CACHE_PREFIX + (this.dataset.cacheKey || 'default');
      this.minChars = parseInt(cfg.minChars, 10) || 2;
      this.perPage = parseInt(cfg.perPage, 10) || 24;
      this.freshTtl = (parseFloat(cfg.freshMinutes) || 0) * 60 * 1000 || FRESH_TTL;
      this.strings = cfg.strings || {};
      this.badgeCfg = cfg.badges || {};
      this.quickBuyCfg = cfg.quickBuy || {};
      this.synonyms = parseSynonyms(cfg.synonyms);
      this.filtersEnabled = cfg.filters !== false;

      if (this.io) {
        this.io.disconnect();
        if (this.sentinel) {
          this.classList.add('gg-io');
          this.io.observe(this.sentinel);
        }
      }
    }

    /* Detect section re-renders (our own renders never detach resultsEl) and
       recover: re-resolve refs, restore control values, repaint from state. */
    watchDom() {
      var self = this;
      this._mo = new MutationObserver(function () {
        if (self.resultsEl && self.resultsEl.isConnected) return;
        self.syncRefs();
        self.restoreControls();
        if (self._modalOpen && self.modal) {
          self.modal.hidden = false;
          self.modal.classList.add('is-open');
        }
        if (self.ms) {
          self.state.visible = self.perPage;
          self.run();
        } else {
          self.renderSkeleton();
        }
      });
      this._mo.observe(this, { childList: true, subtree: true });
    }

    /* Search state round-trips through the URL: refresh keeps progress and
       links share the exact view. Defaults are omitted to keep URLs clean. */
    readStateFromUrl() {
      var st = this.state;
      try {
        var sp = new URLSearchParams(window.location.search);
        var q = sp.get('q');
        if (q && q !== '*') st.q = q;
        var sort = sp.get('sort');
        if (['relevance', 'price-asc', 'price-desc', 'newest', 'az'].indexOf(sort) !== -1) {
          st.sort = sort;
          this._sortExplicit = true;
        }
        function readSet(key, set) {
          var v = sp.get(key);
          if (v) v.split(',').forEach(function (x) { if (x) set.add(x); });
        }
        readSet('supp', st.supp);
        readSet('merch', st.merch);
        readSet('avail', st.avail);
        readSet('flavor', st.flavor);
        var pmin = parseFloat(sp.get('pmin'));
        var pmax = parseFloat(sp.get('pmax'));
        if (!isNaN(pmin)) st.priceMin = Math.max(0, pmin);
        if (!isNaN(pmax)) st.priceMax = Math.max(0, pmax);
        // Collection ids load async — resolved once the catalog is up
        this._pendingCollection = sp.get('col') || null;
      } catch (e) { /* noop */ }
    }

    restoreControls() {
      if (this.input) {
        this.input.value = this.state.q || '';
        if (this.clearBtn) this.clearBtn.hidden = !this.input.value;
      }
      if (this.sortEl) this.sortEl.value = this.state.sort;
      if (this.priceMinEl && this.state.priceMin != null) this.priceMinEl.value = this.state.priceMin;
      if (this.priceMaxEl && this.state.priceMax != null) this.priceMaxEl.value = this.state.priceMax;
    }

    applyPendingCollection() {
      var self = this;
      var handle = this._pendingCollection;
      if (!handle) return;
      this._pendingCollection = null;
      var tile = this.querySelector('[data-gg-featured-collection="' + handle + '"]');
      var label = (tile && tile.getAttribute('data-gg-featured-label')) || handle;
      this.fetchCollectionIds(handle).then(function (ids) {
        if (self._destroyed) return;
        self.state.collection = { handle: handle, label: label, ids: ids };
        self.run();
      }).catch(function () { /* unknown collection in URL — ignore */ });
    }

    /* ---------- data loading ---------- */

    /* IMPORTANT: no Accept: application/json header on these fetches.
       Shopify content-negotiates /collections (and friends): an Accept JSON
       header makes it serve the legacy REST payload and ignore ?view=, which
       has a totally different shape than our Liquid feed. */
    fetchPage(page, extra) {
      var url = this.endpoint + '?view=search-data&q=*&page=' + page + (extra || '&type=product%2Cpage');
      return fetch(url).then(function (res) {
        if (!res.ok) throw new Error('search-data fetch failed: ' + res.status);
        return res.json();
      });
    }

    /* Whole catalog as one item list. Shopify search returns at most 1,000
       results for a query -- past that, products ranked lower silently never
       arrive (a whole drop went missing this way). Under the cap it's the plain
       paged fetch; at the cap it refetches in price bands fetched in parallel
       (doubling widths, since most of the catalog is cheap), halving any band
       that is still capped. Bands overlap on multi-variant products; callers
       dedupe by id. */
    fetchCatalog() {
      var self = this;
      var CAP = 1000;

      function allPages(first, extra) {
        var pages = Math.min(first.pages || 1, MAX_PAGES);
        var rest = [];
        for (var p = 2; p <= pages; p++) rest.push(self.fetchPage(p, extra).catch(function () { return null; }));
        return Promise.all(rest).then(function (results) {
          var items = (first.items || []).slice();
          results.forEach(function (r) { if (r) items = items.concat(r.items || []); });
          return items;
        });
      }

      function priceSlice(lo, hi) { // cents, inclusive
        var extra = '&type=product&filter.v.price.gte=' + (lo / 100).toFixed(2) + '&filter.v.price.lte=' + (hi / 100).toFixed(2);
        return self.fetchPage(1, extra).then(function (first) {
          if ((first.total || 0) >= CAP && hi > lo) {
            var mid = Math.floor((lo + hi) / 2);
            return Promise.all([priceSlice(lo, mid), priceSlice(mid + 1, hi)]).then(function (r) { return r[0].concat(r[1]); });
          }
          return allPages(first, extra);
        });
      }

      return this.fetchPage(1).then(function (first) {
        if ((first.total || 0) < CAP) return allPages(first);
        var jobs = [self.fetchPage(1, '&type=page').then(function (f) { return allPages(f, '&type=page'); })];
        // Outside the primary market Shopify has no price filter (price_max 0) and
        // silently ignores filter.v.price, so every band stays capped and halving
        // recursed into thousands of requests until rate limiting killed the load.
        // Split on availability instead: all in-stock products always arrive.
        // ponytail: out-of-stock half is still capped at 1,000 there; partition it
        // further (another filter that exists in every market) if that ever matters.
        if (!first.price_max) {
          ['1', '0'].forEach(function (v) {
            var extra = '&type=product&filter.v.availability=' + v;
            jobs.push(self.fetchPage(1, extra).then(function (f) { return allPages(f, extra); }));
          });
          return Promise.all(jobs).then(function (r) { return [].concat.apply([], r); });
        }
        var max = first.price_max;
        var lo = 0;
        for (var f = 0.01; lo <= max; f *= 2) {
          var hi = f >= 1 ? max : Math.floor(max * f);
          if (hi < lo) continue;
          jobs.push(priceSlice(lo, hi));
          lo = hi + 1;
        }
        return Promise.all(jobs).then(function (r) { return [].concat.apply([], r); });
      });
    }

    fetchCollectionsPage(page) {
      var url = this.collectionsEndpoint + '?view=search-data&page=' + page;
      return fetch(url).then(function (res) {
        if (!res.ok) throw new Error('collections feed fetch failed: ' + res.status);
        return res.json();
      }).then(function (data) {
        // Guard against the legacy REST shape: our feed entries carry `url`.
        var list = data && data.collections;
        if (!Array.isArray(list) || (list.length && !list[0].url)) {
          throw new Error('unexpected collections payload shape');
        }
        return data;
      });
    }

    /* Lazy per-collection product-ID fetch for the quick-filter tabs.
       Cached in memory for the session — a tab is ~one small request once. */
    fetchCollectionIds(handle) {
      var self = this;
      if (!this._collectionIds) this._collectionIds = {};
      if (this._collectionIds[handle]) return Promise.resolve(this._collectionIds[handle]);
      function pageUrl(p) { return self.collectionsEndpoint + '/' + handle + '?view=search-ids&page=' + p; }
      return fetch(pageUrl(1)).then(function (res) {
        if (!res.ok) throw new Error('collection ids fetch failed: ' + res.status);
        return res.json();
      }).then(function (first) {
        var ids = first.ids || [];
        var pages = Math.min(first.pages || 1, 4);
        var rest = [];
        for (var p = 2; p <= pages; p++) {
          rest.push(fetch(pageUrl(p)).then(function (r) { return r.json(); }).catch(function () { return null; }));
        }
        return Promise.all(rest).then(function (results) {
          results.forEach(function (r) { if (r && r.ids) ids = ids.concat(r.ids); });
          var set = new Set(ids);
          self._collectionIds[handle] = set;
          return set;
        });
      });
    }

    readCache() {
      try {
        var raw = localStorage.getItem(this.cacheKey);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        if (!parsed || !parsed.ts || Date.now() - parsed.ts > CACHE_TTL) return null;
        if (!Array.isArray(parsed.products) || !parsed.products.length) return null;
        return parsed;
      } catch (e) {
        return null;
      }
    }

    writeCache() {
      this.lastLoadTs = Date.now();
      try {
        localStorage.setItem(this.cacheKey, JSON.stringify({
          ts: this.lastLoadTs,
          products: this.products,
          pages: this.pages,
          collections: this.collections
        }));
      } catch (e) { /* quota or private mode — skip */ }
    }

    init() {
      var self = this;
      var cached = this.readCache();
      if (cached) {
        this.absorb(cached.products);
        this.pages = cached.pages || [];
        this.collections = cached.collections || [];
        this.buildAuxIndices();
        this.partial = false;
        this.lastLoadTs = cached.ts;
        this.run();
        this.applyPendingCollection();
        // Stale-while-revalidate: render from cache instantly, refresh behind it.
        if (Date.now() - cached.ts > this.freshTtl) this.refresh();
        return;
      }

      // Collections feed is best-effort — search still works if it fails.
      var collectionsDone = this.fetchCollectionsPage(1).then(function (first) {
        var all = first.collections || [];
        var pages = Math.min(first.pages || 1, MAX_PAGES);
        var rest = [];
        for (var p = 2; p <= pages; p++) rest.push(self.fetchCollectionsPage(p).catch(function () { return null; }));
        return Promise.all(rest).then(function (results) {
          results.forEach(function (r) { if (r && r.collections) all = all.concat(r.collections); });
          self.collections = all;
        });
      }).catch(function () { self.collections = []; });

      // Results render once the whole catalog is indexed: rendering partial
      // batches reshuffled the grid as each one landed. The rotating loader
      // lines cover the wait so a cold load never looks stuck.
      this.fetchCatalog().then(function (items) {
        if (self._destroyed) return;
        self.absorbItems(items);
        return collectionsDone.then(function () {
          if (self._destroyed) return;
          self.buildAuxIndices();
          self.partial = false;
          self.writeCache();
          self.run();
          self.applyPendingCollection();
        });
      }).catch(function (err) {
        console.error('[gg-instant-search]', err);
        self.showError();
      });
    }

    /* Background revalidation: refetch the full catalog, build fresh indices
       off to the side, then atomically swap and re-run the current query.
       The user keeps interacting with the cached index the whole time. */
    refresh() {
      var self = this;
      if (this._refreshing) return;
      this._refreshing = true;

      var collectionsP = this.fetchCollectionsPage(1).then(function (first) {
        var all = first.collections || [];
        var pages = Math.min(first.pages || 1, MAX_PAGES);
        var rest = [];
        for (var p = 2; p <= pages; p++) rest.push(self.fetchCollectionsPage(p).catch(function () { return null; }));
        return Promise.all(rest).then(function (results) {
          results.forEach(function (r) { if (r && r.collections) all = all.concat(r.collections); });
          return all;
        });
      }).catch(function () { return self.collections; });

      var itemsP = this.fetchCatalog();

      Promise.all([itemsP, collectionsP]).then(function (res) {
        if (self._destroyed) return;
        var products = [];
        var pages = [];
        var byId = new Map();
        res[0].forEach(function (it) {
          if (!it) return;
          if (it.object_type === 'page') pages.push(it);
          else if (!byId.has(it.id)) { byId.set(it.id, it); products.push(it); }
        });
        if (!products.length) throw new Error('empty catalog on refresh');

        self.ms = buildMiniIndex(products, ['title', 'joined', 'type', 'tags', 'flavors', 'vendor'],
          { title: 10, joined: 8, type: 4, tags: 3, flavors: 4, vendor: 1 });
        self.products = products;
        self._renderKey = null; // fresh data under the same ids must repaint
        self.byId = byId;
        self.pages = pages;
        self.collections = res[1] || [];
        self.buildAuxIndices();
        self.writeCache();
        self.run();
      }).catch(function (err) {
        console.warn('[gg-instant-search] background refresh failed — keeping cached index', err);
      }).then(function () {
        self._refreshing = false;
      });
    }

    absorbItems(items) {
      var self = this;
      var products = [];
      items.forEach(function (item) {
        if (!item) return;
        if (item.object_type === 'page') {
          self.pages.push(item);
        } else {
          products.push(item);
        }
      });
      this.absorb(products);
    }

    absorb(products) {
      var self = this;
      if (!this.ms) {
        this.ms = buildMiniIndex([], ['title', 'joined', 'type', 'tags', 'flavors', 'vendor'],
          { title: 10, joined: 8, type: 4, tags: 3, flavors: 4, vendor: 1 });
      }
      // byId is updated inside the filter so repeats within one batch are
      // dropped too (price slices overlap on multi-variant products).
      var fresh = products.filter(function (p) {
        if (!p || self.byId.has(p.id)) return false;
        self.byId.set(p.id, p);
        return true;
      });
      this.products = this.products.concat(fresh);
      if (fresh.length) this.ms.addAll(fresh);
    }

    buildAuxIndices() {
      var seen = new Set();
      this.pages = this.pages.filter(function (p) {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
      this.msCollections = buildMiniIndex(
        this.collections.map(function (c, i) { return { id: i, title: c.title, joined: c.title }; }),
        ['title', 'joined'], { title: 2, joined: 2 }
      );
      this.msPages = buildMiniIndex(
        this.pages.map(function (p, i) { return { id: i, title: p.title, joined: p.title }; }),
        ['title', 'joined'], { title: 2, joined: 2 }
      );
    }

    /* ---------- search core ---------- */

    // Expanded term -> indices of the original query tokens it stands for.
    expandTerms(q) {
      var tokens = tokenize(q);
      var map = new Map();
      function add(term, idxs) {
        if (!term) return;
        var s = map.get(term);
        if (!s) { s = new Set(); map.set(term, s); }
        idxs.forEach(function (i) { s.add(i); });
      }

      tokens.forEach(function (t, i) { add(t, [i]); });
      for (var i = 0; i < tokens.length - 1; i++) add(tokens[i] + tokens[i + 1], [i, i + 1]);

      this.synonyms.forEach(function (group) {
        var hitIdxs = [];
        group.forEach(function (term) {
          var tTokens = tokenize(term);
          var at = indexOfSeq(tokens, tTokens);
          if (at !== -1) {
            for (var k = 0; k < tTokens.length; k++) hitIdxs.push(at + k);
          } else if (tTokens.length > 1) {
            var j = tokens.indexOf(tTokens.join(''));
            if (j !== -1) hitIdxs.push(j);
          }
        });
        if (!hitIdxs.length) return;
        var idxs = Array.from(new Set(hitIdxs));
        group.forEach(function (term) {
          var tTokens = tokenize(term);
          tTokens.forEach(function (t) { add(t, idxs); });
          if (tTokens.length > 1) add(tTokens.join(''), idxs);
        });
      });

      /* Strictness depends on a term's origin. Typed tokens get typo tolerance
         (≥4 chars) and the one being typed gets prefix matching. Synonym and
         joined-pair expansions must match exactly — fuzzy/prefix on short
         fragments like "mat" or "pad" pulls in "meat", "hat", "Matara". */
      var originals = new Set(tokens);
      var last = tokens[tokens.length - 1];
      var searchOpts = {
        prefix: function (term) { return term === last; },
        fuzzy: function (term) { return originals.has(term) && term.length >= 4 ? 0.2 : false; }
      };

      return { tokens: tokens, map: map, query: Array.from(map.keys()).join(' '), searchOpts: searchOpts };
    }

    searchProducts(expanded) {
      var self = this;
      if (!expanded.tokens.length) return this.products.slice();

      var raw = this.ms.search(expanded.query, expanded.searchOpts);

      // Precision pass: prefer results covering every original token, then majority.
      var need = expanded.tokens.length;
      var scored = raw.map(function (r) {
        var qts = r.queryTerms || r.terms || [];
        var covered = new Set();
        qts.forEach(function (t) {
          var s = expanded.map.get(t);
          if (s) s.forEach(function (i) { covered.add(i); });
        });
        return { r: r, cov: covered.size };
      });

      var keep = scored.filter(function (x) { return x.cov >= need; });
      if (!keep.length) keep = scored.filter(function (x) { return x.cov >= Math.ceil(need / 2); });
      if (!keep.length) keep = scored;

      return keep
        .map(function (x) { return self.byId.get(x.r.id); })
        .filter(Boolean);
    }

    searchAux(index, source, expanded, limit) {
      if (!index || !expanded.tokens.length) return [];
      return index.search(expanded.query, expanded.searchOpts)
        .slice(0, limit)
        .map(function (r) { return source[r.id]; })
        .filter(Boolean);
    }

    applyFilters(list, skip) {
      var st = this.state;
      return list.filter(function (p) {
        if (skip !== 'collection' && st.collection && st.collection.ids && !st.collection.ids.has(p.id)) return false;
        if (skip !== 'supp' && st.supp.size) {
          var supps = suppTypesOf(p);
          var suppHit = false;
          for (var s = 0; s < supps.length; s++) {
            if (st.supp.has(supps[s])) { suppHit = true; break; }
          }
          if (!suppHit) return false;
        }
        if (skip !== 'merch' && st.merch.size && !st.merch.has(p.merch_type || '')) return false;
        if (skip !== 'avail' && st.avail.size && !st.avail.has(availabilityOf(p))) return false;
        if (skip !== 'flavor' && st.flavor.size) {
          var keys = flavorKeys(p);
          var hit = false;
          for (var i = 0; i < keys.length; i++) {
            if (st.flavor.has(keys[i])) { hit = true; break; }
          }
          if (!hit) return false;
        }
        if (skip !== 'price') {
          if (st.priceMin != null && p.price < st.priceMin * 100) return false;
          if (st.priceMax != null && p.price > st.priceMax * 100) return false;
        }
        return true;
      });
    }

    applySort(list, searching) {
      var sorted = list.slice();
      var demoteGone = false;
      switch (this.state.sort) {
        case 'price-asc': sorted.sort(function (a, b) { return a.price - b.price; }); break;
        case 'price-desc': sorted.sort(function (a, b) { return b.price - a.price; }); break;
        case 'az': sorted.sort(function (a, b) { return a.title.localeCompare(b.title); }); break;
        case 'newest':
          sorted.sort(function (a, b) { return (b.created || 0) - (a.created || 0); });
          demoteGone = true;
          break;
        default:
          // Relevance with no query falls back to newest order.
          if (!searching) sorted.sort(function (a, b) { return (b.created || 0) - (a.created || 0); });
          demoteGone = true;
          break;
      }
      if (demoteGone) {
        // Over 75% of the catalog is sold out — buyable products surface first.
        var buyable = [];
        var gone = [];
        sorted.forEach(function (p) { (isBuyable(p) ? buyable : gone).push(p); });
        sorted = buyable.concat(gone);
      }
      return sorted;
    }

    run() {
      // No renders until the catalog is complete — typing during the initial
      // load updates state.q and the final run picks it up.
      if (!this.ms || this.partial) return;
      this.stopLoader();

      // Active use revalidates too — someone searching for 20 minutes straight
      // shouldn't be stuck on the index they arrived with.
      if (!this.partial && this.lastLoadTs && Date.now() - this.lastLoadTs > this.freshTtl) {
        this.refresh();
      }

      var q = (this.state.q || '').trim();
      var searching = q.length >= this.minChars;

      // Until the shopper picks a sort themselves: browse shows Newest,
      // querying ranks by Relevance. The select follows along.
      if (!this._sortExplicit) {
        var autoSort = searching ? 'relevance' : 'newest';
        if (this.state.sort !== autoSort) {
          this.state.sort = autoSort;
          if (this.sortEl) this.sortEl.value = autoSort;
        }
      }

      var expanded = searching ? this.expandTerms(q) : null;

      var base = searching ? this.searchProducts(expanded) : this.products.slice();
      this.currentBase = base;
      this.currentResults = this.applySort(this.applyFilters(base), searching);

      // Featured collection tiles belong to the browse state only.
      if (this.featuredEl) this.featuredEl.hidden = searching;
      this.syncFeaturedActive();

      // Aux rows must never take down product results.
      try {
        this.renderAux(searching ? expanded : null);
      } catch (e) {
        console.error('[gg-instant-search] aux render failed', e);
      }
      this.renderFilters(base);
      this.renderResults(q);
      this.renderCount(q);
      this.updateUrl(q);
    }

    /* ---------- rendering ---------- */

    renderSkeleton() {
      if (!this.resultsEl) return;
      var html = '<div class="gg-search-loader" role="status" aria-live="polite">' +
        '<span class="gg-search-loader__dot" aria-hidden="true"></span>' +
        '<span class="gg-search-loader__msg" data-gg-loader-msg></span></div>';
      // Two rows on a 6-column desktop grid; CSS caps mobile at 6. --i
      // staggers the shimmer so it sweeps across the shelf.
      for (var i = 0; i < 12; i++) {
        html += '<div class="gg-search-card gg-search-card--skeleton" style="--i:' + i + '"><div class="gg-search-card__media"></div><div class="gg-search-card__info"><div class="gg-skeleton-line"></div><div class="gg-skeleton-line gg-skeleton-line--short"></div></div></div>';
      }
      this.resultsEl.innerHTML = html;
      this._renderKey = null;
      this.startLoader();
    }

    /* Rotating "browsing the store" lines while a cold load runs. Only shown
       on a cold load: a cached index renders before the first line swaps. */
    startLoader() {
      var self = this;
      var lines = this.strings.loadingLines || [
        'Checking the shelves',
        'Digging through the back room',
        'Counting tubs',
        'Rinsing the shakers',
        'Folding the merch',
        'Almost there'
      ];
      var n = 0;
      var paint = function () {
        var el = self.resultsEl && self.resultsEl.querySelector('[data-gg-loader-msg]');
        if (!el) return self.stopLoader();
        el.textContent = lines[Math.min(n, lines.length - 1)] + '\u2026';
        el.classList.remove('is-in');
        void el.offsetWidth; // restart the fade for each line
        el.classList.add('is-in');
        n++;
      };
      this.stopLoader();
      paint();
      this._loaderTimer = setInterval(paint, 1300);
    }

    stopLoader() {
      if (this._loaderTimer) clearInterval(this._loaderTimer);
      this._loaderTimer = null;
    }

    /* Same classes as snippets/product-status-badges.liquid so theme CSS +
       admin badge settings apply. Priority: promotion > sold out (stacks with
       status) > status > tag badge > custom metafield > sale. */
    badgeHtml(p) {
      var b = this.badgeCfg;
      if (p.status === 'hide_badge') return '';

      function item(mod, text, style) {
        return '<div class="product__badge__item product__badge__item--' + mod + '"' + (style ? ' style="' + escapeHtml(style) + '"' : '') + '><span>' + escapeHtml(text) + '</span></div>';
      }

      var items = '';
      if (b.promotion && b.promotionText) {
        items = item('promotion', b.promotionText);
      } else if (b.enabled !== false) {
        var statusMods = {
          live_drop: ['live-drop', b.liveDrop || 'Live Drop'],
          pre_order: ['pre-order', b.preOrder || 'Pre-Order'],
          pre_order_ended: ['pre-order-ended', b.preOrderEnded || 'Pre-Order Ended'],
          coming_soon: ['coming-soon', b.comingSoon || 'Coming Soon'],
          gift_addon: ['gift-addon', b.giftAddon || 'Gift with Purchase']
        };
        var status = statusMods[p.status] || null;
        var stacksSoldOut = status && p.status !== 'gift_addon';
        var tags = (p.tags || []).map(function (t) { return String(t).trim().toLowerCase(); });
        var tagBadge = (b.tags || []).filter(function (t) { return t.tag && tags.indexOf(t.tag) !== -1; })[0];
        var hasCustom = !!p.badge;
        var soldOut = !p.available;

        if (soldOut && (stacksSoldOut || (!status && !hasCustom))) {
          items += item('sold', b.soldOut || 'Sold Out');
        }
        if (status) {
          items += item(status[0], status[1]);
        } else if (tagBadge) {
          items += item('tag', tagBadge.text, '--tag-badge-bg: ' + tagBadge.bg + '; --tag-badge-text: ' + tagBadge.color + ';');
        } else if (hasCustom) {
          items += item('custom', p.badge);
        } else if (!soldOut && b.showSale !== false && p.compare_at > p.price) {
          items += item('sale', b.sale || 'Sale');
        }
      }

      return items ? '<div class="product__badge">' + items + '</div>' : '';
    }

    /* Quick buy mirrors snippets/product-quick-buy.liquid markup exactly —
       theme.js handles the submit (AJAX add + cart drawer) and the quick-view
       modal for multi-variant products via its own delegated listeners. */
    quickBuyHtml(p) {
      var qb = this.quickBuyCfg;
      if (!qb || qb.mode === 'none') return '';
      if (!isBuyable(p) || !p.variant_id) return '';
      var single = p.variants === 1 && (!p.selling_plans || qb.subsSelectors === false);
      var inner = '<span class="btn__inner">' + CART_ICON
        + '<span class="btn__text">' + (single ? 'Add to cart' : 'Choose options') + '</span>'
        + '<span class="btn__loader"><svg height="18" width="18" class="svg-loader"><circle r="7" cx="9" cy="9"/><circle stroke-dasharray="87.96459430051421 87.96459430051421" r="7" cx="9" cy="9"/></svg></span>'
        + '</span>';
      if (single) {
        return '<div class="product-grid-item__quick-buy gg-search-card__quick">'
          + '<form method="post" action="/cart/add" class="quick__form" data-product-form data-quickbuy-form>'
          + '<input type="hidden" name="quantity" value="1">'
          + '<input type="hidden" name="id" value="' + p.variant_id + '">'
          + '<button type="submit" name="add" aria-label="Add to cart" class="btn--quick" data-add-to-cart data-atc-trigger>' + inner + '</button>'
          + '<div class="product-grid-item__error" role="alert" data-cart-errors-container></div>'
          + '</form></div>';
      }
      // Multi-variant: prefer the theme's own quick-view popup (exported from
      // the bundle as theme.QuickViewPopup — see the surgical patch note in
      // the section header comment). Falls back to our sheet, then the href.
      var themeQv = !!(window.theme && window.theme.QuickViewPopup);
      return '<div class="product-grid-item__quick-buy gg-search-card__quick">'
        + '<a class="btn--quick" href="' + escapeHtml(p.url) + '" aria-label="Choose options" data-handle="' + escapeHtml(p.handle) + '" '
        + (themeQv ? 'data-button-quick-view' : 'data-gg-qv')
        + '>' + inner + '</a>'
        + '</div>';
    }

    /* The theme's QuickViewPopup binds per-container with a data-initialized
       guard — safe to construct after every render; fresh buttons get bound,
       already-bound ones are skipped. */
    bindThemeQuickView() {
      if (!window.theme || !window.theme.QuickViewPopup || !this.resultsEl) return;
      if (!this.resultsEl.querySelector('[data-button-quick-view]:not([data-initialized])')) return;
      try {
        new window.theme.QuickViewPopup(this.resultsEl);
      } catch (e) {
        console.warn('[gg-instant-search] theme quick view bind failed', e);
      }
    }

    /* ---------- quick view (variant picker sheet) ---------- */

    openQuickView(handle, url) {
      var self = this;
      if (!this.qvModal || !this.qvBody) { window.location.href = url; return; }
      this._qvOpen = true;
      this.qvModal.hidden = false;
      var modal = this.qvModal;
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
      this.lockScroll();
      this.qvBody.innerHTML = '<div class="gg-qv__loading"><div class="gg-skeleton-line"></div><div class="gg-skeleton-line gg-skeleton-line--short"></div></div>';

      if (!this._qvCache) this._qvCache = {};
      var cached = this._qvCache[handle];
      var load = cached
        ? Promise.resolve(cached)
        : fetch(url + '?view=search-variants').then(function (res) {
            if (!res.ok) throw new Error('variant feed failed: ' + res.status);
            return res.json();
          }).then(function (data) {
            self._qvCache[handle] = data;
            return data;
          });

      load.then(function (data) {
        if (!self._qvOpen) return;
        self.renderQuickView(data);
      }).catch(function () {
        window.location.href = url; // graceful fallback: go to the product page
      });
    }

    closeQuickView() {
      if (!this.qvModal || !this._qvOpen) return;
      this._qvOpen = false;
      var self = this;
      this.qvModal.classList.remove('is-open');
      this.unlockScroll();
      setTimeout(function () {
        if (!self._qvOpen && self.qvModal) self.qvModal.hidden = true;
      }, 260);
    }

    renderQuickView(data) {
      // Default selection: first available variant, else first variant
      var current = data.variants.filter(function (v) { return v.available; })[0] || data.variants[0];
      this._qvData = data;
      this._qvSelection = current ? current.options.slice() : [];

      var img = imgSrc(current && current.image || data.image);
      var optsHtml = data.options.map(function (opt, oi) {
        if (opt.values.length < 2 && data.options.length === 1) return '';
        return '<div class="gg-qv__opt"><span class="gg-filter-group__label label-typography">' + escapeHtml(opt.name) + '</span>'
          + '<div class="gg-qv__opt-values">'
          + opt.values.map(function (val) {
            return '<button type="button" class="gg-chip" data-gg-qv-opt data-oi="' + oi + '" data-value="' + escapeHtml(val) + '">' + escapeHtml(val) + '</button>';
          }).join('')
          + '</div></div>';
      }).join('');

      this.qvBody.innerHTML =
        '<div class="gg-qv">'
        + '<div class="gg-qv__media">' + (img ? '<img class="gg-qv__img" src="' + escapeHtml(img) + '" alt="">' : '') + '</div>'
        + '<div class="gg-qv__content">'
        + '<p class="gg-qv__title">' + escapeHtml(data.title) + '</p>'
        + '<p class="gg-qv__price" data-gg-qv-price></p>'
        + optsHtml
        + '<form method="post" action="/cart/add" class="quick__form gg-qv__form" data-product-form data-quickbuy-form>'
        + '<input type="hidden" name="quantity" value="1">'
        + '<input type="hidden" name="id" value="" data-gg-qv-id>'
        + '<button type="submit" name="add" class="btn btn--primary btn--solid gg-qv__atc" data-add-to-cart data-atc-trigger>'
        + '<span class="btn__inner">' + CART_ICON + '<span class="btn__text">Add to cart</span>'
        + '<span class="btn__loader"><svg height="18" width="18" class="svg-loader"><circle r="7" cx="9" cy="9"/><circle stroke-dasharray="87.96459430051421 87.96459430051421" r="7" cx="9" cy="9"/></svg></span></span>'
        + '</button>'
        + '<div class="product-grid-item__error" role="alert" data-cart-errors-container></div>'
        + '</form>'
        + '<a class="gg-qv__details" href="' + escapeHtml(data.url) + '">View full details →</a>'
        + '</div></div>';

      this.syncQuickViewVariant();
    }

    syncQuickViewVariant() {
      var data = this._qvData;
      var sel = this._qvSelection;
      if (!data || !this.qvBody) return;
      var variant = data.variants.filter(function (v) {
        return v.options.every(function (o, i) { return o === sel[i]; });
      })[0] || null;

      this.qvBody.querySelectorAll('[data-gg-qv-opt]').forEach(function (chip) {
        var oi = parseInt(chip.getAttribute('data-oi'), 10);
        chip.classList.toggle('is-active', sel[oi] === chip.getAttribute('data-value'));
      });

      var priceEl = this.qvBody.querySelector('[data-gg-qv-price]');
      var idEl = this.qvBody.querySelector('[data-gg-qv-id]');
      var atc = this.qvBody.querySelector('[data-gg-qv-id]') && this.qvBody.querySelector('.gg-qv__atc');
      var imgEl = this.qvBody.querySelector('.gg-qv__img');

      if (variant) {
        if (priceEl) {
          priceEl.innerHTML = escapeHtml(variant.price_formatted)
            + (variant.compare_at > variant.price && variant.compare_at_formatted
              ? ' <s class="gg-search-card__compare">' + escapeHtml(variant.compare_at_formatted) + '</s>' : '');
        }
        if (idEl) idEl.value = variant.id;
        if (atc) {
          atc.disabled = !variant.available;
          var text = atc.querySelector('.btn__text');
          if (text) text.textContent = variant.available ? 'Add to cart' : 'Sold out';
        }
        if (imgEl && variant.image) imgEl.src = imgSrc(variant.image);
      } else {
        if (priceEl) priceEl.textContent = '';
        if (atc) {
          atc.disabled = true;
          var t = atc.querySelector('.btn__text');
          if (t) t.textContent = 'Unavailable';
        }
      }
    }

    cardHtml(p, index) {
      var hidePrice = (p.tags || []).indexOf('hide-price') !== -1;
      var url = escapeHtml(p.url);
      var img = imgSrc(p.image);
      var img2 = this.hoverCapable ? imgSrc(p.image2) : '';
      var media = img
        ? '<img class="gg-search-card__img" loading="lazy" src="' + escapeHtml(img) + '" alt="' + escapeHtml(p.title) + '">'
          + (img2 ? '<img class="gg-search-card__img gg-search-card__img--alt" loading="lazy" src="' + escapeHtml(img2) + '" alt="" aria-hidden="true">' : '')
        : '<span class="gg-search-card__noimg" aria-hidden="true">GG</span>';
      var priceHtml = '';
      if (!hidePrice) {
        priceHtml = '<div class="gg-search-card__price">'
          + (p.price_varies ? '<span class="gg-search-card__from">From</span> ' : '')
          + escapeHtml(p.price_formatted)
          + (p.compare_at > p.price && p.compare_at_formatted ? ' <s class="gg-search-card__compare">' + escapeHtml(p.compare_at_formatted) + '</s>' : '')
          + '</div>';
      }
      // Staggered reveal, capped so deep grids don't wait on long delays.
      // Card root is a div (not <a>) — quick-buy forms can't nest in anchors;
      // an overlay link inside the media keeps the whole image clickable.
      var delay = typeof index === 'number' ? Math.min(index, 11) * 30 : 0;
      return '<div class="gg-search-card" style="animation-delay:' + delay + 'ms">'
        + '<div class="gg-search-card__media">'
        + '<a class="gg-search-card__link" href="' + url + '" tabindex="-1" aria-hidden="true"></a>'
        + media + this.badgeHtml(p) + this.quickBuyHtml(p)
        + '</div>'
        + '<div class="gg-search-card__info">'
        + '<a class="gg-search-card__info-link" href="' + url + '">'
        + '<p class="gg-search-card__title">' + escapeHtml(p.title) + '</p>'
        + priceHtml
        + '</a>'
        + '</div></div>';
    }

    renderAux(expanded) {
      var self = this;

      if (this.collectionsEl) {
        var cols = expanded ? this.searchAux(this.msCollections, this.collections, expanded, 8) : [];
        if (cols.length) {
          this.collectionsEl.innerHTML =
            '<span class="gg-aux__label label-typography">' + escapeHtml(this.strings.collectionsLabel || 'Collections') + '</span>'
            + ARROW_PREV
            + '<div class="gg-aux__rail" data-gg-rail>'
            + cols.map(function (c) {
              var thumb = imgSrc(c.image);
              return '<a class="gg-chip gg-chip--collection" href="' + escapeHtml(c.url) + '">'
                + (thumb ? '<img class="gg-chip__thumb" loading="lazy" src="' + escapeHtml(thumb) + '" alt="">' : '')
                + escapeHtml(c.title)
                + (c.count != null ? ' <span class="gg-chip__count">' + c.count + '</span>' : '')
                + '</a>';
            }).join('')
            + '</div>'
            + ARROW_NEXT;
        }
        this.collectionsEl.hidden = !cols.length;
      }

      if (this.pagesEl) {
        var pages = expanded ? this.searchAux(this.msPages, this.pages, expanded, 6) : [];
        if (pages.length) {
          this.pagesEl.innerHTML =
            '<span class="gg-aux__label label-typography">' + escapeHtml(this.strings.pagesLabel || 'Pages') + '</span>'
            + ARROW_PREV
            + '<div class="gg-aux__rail" data-gg-rail>'
            + pages.map(function (p) {
              return '<a class="gg-chip gg-chip--page" href="' + escapeHtml(p.url) + '">'
                + '<svg width="12" height="14" viewBox="0 0 12 14" fill="none" aria-hidden="true"><path d="M2 1h5l3 3v9H2V1z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M7 1v3h3" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>'
                + escapeHtml(p.title)
                + '</a>';
            }).join('')
            + '</div>'
            + ARROW_NEXT;
        }
        this.pagesEl.hidden = !pages.length;
      }

      this.syncRails();
    }

    /* Overflow state drives the trailing fade, grab cursor, and arrows. */
    syncRails() {
      var self = this;
      this.querySelectorAll('[data-gg-rail]').forEach(function (rail) {
        self.syncRailState(rail);
      });
    }

    syncRailState(rail) {
      var overflowing = rail.scrollWidth > rail.clientWidth + 1;
      var atStart = rail.scrollLeft <= 4;
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      rail.classList.toggle('is-overflowing', overflowing);
      rail.classList.toggle('at-end', atEnd);
      var aux = rail.parentElement;
      var prev = aux && aux.querySelector('[data-gg-rail-prev]');
      var next = aux && aux.querySelector('[data-gg-rail-next]');
      if (prev) { prev.hidden = !overflowing; prev.disabled = atStart; }
      if (next) { next.hidden = !overflowing; next.disabled = atEnd; }
    }

    renderResults(q) {
      if (!this.resultsEl) return;
      var visible = this.currentResults.slice(0, this.state.visible);
      var hasResults = visible.length > 0;

      // Same cards in the same order: leave the DOM alone. Rebuilding tears
      // down every image, which is what flickered as catalog batches landed.
      var renderKey = visible.map(function (p) { return p.id; }).join(',');
      if (renderKey !== this._renderKey || !hasResults) {
        this.resultsEl.innerHTML = visible.map(this.cardHtml, this).join('');
        this._renderKey = renderKey;
      }
      this.resultsEl.hidden = !hasResults;

      if (this.emptyEl) {
        var auxVisible = (this.collectionsEl && !this.collectionsEl.hidden) || (this.pagesEl && !this.pagesEl.hidden);
        this.emptyEl.hidden = hasResults || auxVisible;
        var msgEl = this.emptyEl.querySelector('[data-gg-empty-message]');
        if (msgEl && !hasResults) {
          var template = this.strings.noResults || 'No results for “{query}”. Try a different search.';
          msgEl.textContent = template.replace('{query}', q);
        }
      }
      if (this.moreBtn) {
        this.moreBtn.hidden = this.currentResults.length <= this.state.visible;
      }
      if (this.errorEl) this.errorEl.hidden = true;
      this.bindThemeQuickView();
    }

    expand() {
      if (!this.resultsEl || !this.currentResults || this.currentResults.length <= this.state.visible) return;
      var prev = this.state.visible;
      this.state.visible += this.perPage;
      var slice = this.currentResults.slice(prev, this.state.visible);
      this.resultsEl.insertAdjacentHTML('beforeend', slice.map(this.cardHtml, this).join(''));
      if (this.moreBtn) this.moreBtn.hidden = this.currentResults.length <= this.state.visible;
      this.bindThemeQuickView();
    }

    renderCount(q) {
      if (!this.countEl) return;
      var st = this.state;
      var n = this.currentResults.length;
      var filtered = st.supp.size || st.merch.size || st.avail.size || st.flavor.size || st.collection || st.priceMin != null || st.priceMax != null;
      var text = '';
      if (q.length >= this.minChars) {
        text = n + (n === 1 ? ' result' : ' results') + ' for “' + q + '”';
      } else if (filtered) {
        text = n + ' products';
      }
      // Plain browse shows no count — the full catalog total is just noise.
      this.countEl.textContent = text;
    }

    renderFilters(base) {
      if (!this.filtersEnabled) return;
      var st = this.state;

      var focusKey = null;
      var active = document.activeElement;
      if (active && active.hasAttribute && active.hasAttribute('data-gg-chip')) {
        focusKey = active.getAttribute('data-group') + '|' + active.getAttribute('data-value');
      }

      this.renderFilterMeta();

      if (this.filtersEl) {
        function chipGroup(label, group, activeSet, counts, order, labelFn) {
          var values = order || Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; });
          if (values.length < 2 && !activeSet.size) return '';
          var chips = values.map(function (v) {
            if (!(v in counts) && !activeSet.has(v)) return '';
            var isActive = activeSet.has(v);
            return '<button type="button" class="gg-chip' + (isActive ? ' is-active' : '') + '"'
              + ' data-gg-chip data-group="' + group + '" data-value="' + escapeHtml(v) + '"'
              + ' aria-pressed="' + isActive + '">'
              + escapeHtml(labelFn ? labelFn(v) : v)
              + ' <span class="gg-chip__count">' + (counts[v] || 0) + '</span></button>';
          }).join('');
          return '<div class="gg-filter-group"><span class="gg-filter-group__label label-typography">' + label + '</span><div class="gg-filter-group__chips">' + chips + '</div></div>';
        }

        // Counts for each group are computed with the *other* groups' filters applied.
        var suppCounts = {};
        this.applyFilters(base, 'supp').forEach(function (p) {
          suppTypesOf(p).forEach(function (label) {
            suppCounts[label] = (suppCounts[label] || 0) + 1;
          });
        });
        var merchCounts = {};
        this.applyFilters(base, 'merch').forEach(function (p) {
          if (p.merch_type) merchCounts[p.merch_type] = (merchCounts[p.merch_type] || 0) + 1;
        });
        var availCounts = {};
        this.applyFilters(base, 'avail').forEach(function (p) {
          var a = availabilityOf(p);
          availCounts[a] = (availCounts[a] || 0) + 1;
        });
        var flavorCounts = {};
        this.applyFilters(base, 'flavor').forEach(function (p) {
          flavorKeys(p).forEach(function (k) {
            flavorCounts[k] = (flavorCounts[k] || 0) + 1;
          });
        });

        this.filtersEl.innerHTML =
          chipGroup(this.strings.suppLabel || 'Supp Type', 'supp', st.supp, suppCounts, ['Energy', 'Caffeine Free', 'Sleep Supplement', 'Sleep Supplement + Melatonin', 'Creatine Supplement'])
          + chipGroup(this.strings.merchLabel || 'Merch Type', 'merch', st.merch, merchCounts)
          + chipGroup(this.strings.availabilityLabel || 'Availability', 'avail', st.avail, availCounts, ['Active pre-order', 'Past pre-order', 'In stock', 'Coming soon', 'Sold out'])
          + chipGroup(this.strings.flavorLabel || 'Flavor', 'flavor', st.flavor, flavorCounts, null, titleCase);
        this.filtersEl.hidden = this.filtersEl.innerHTML.trim() === '';
      }

      if (focusKey) {
        var parts = focusKey.split('|');
        var selector = '[data-gg-chip][data-group="' + parts[0] + '"][data-value="' + CSS.escape(parts[1]) + '"]';
        var again = (this.filtersEl && this.filtersEl.querySelector(selector))
          || (this.appliedEl && this.appliedEl.querySelector(selector));
        if (again) again.focus();
      }
    }

    syncFeaturedActive() {
      var st = this.state;
      this.querySelectorAll('[data-gg-featured-collection]').forEach(function (tile) {
        tile.classList.toggle('is-active',
          !!st.collection && tile.getAttribute('data-gg-featured-collection') === st.collection.handle);
      });
    }

    /* Everything filter-related that lives outside the modal body: the Filters
       button badge, the applied-filter chip row, and the modal's live count. */
    renderFilterMeta() {
      var st = this.state;
      var self = this;

      if (this.filtersBadge) {
        var activeCount = st.supp.size + st.merch.size + st.avail.size + st.flavor.size
          + (st.collection ? 1 : 0)
          + (st.priceMin != null ? 1 : 0) + (st.priceMax != null ? 1 : 0);
        this.filtersBadge.textContent = activeCount;
        this.filtersBadge.hidden = !activeCount;
      }

      if (this.modalCount) {
        var n = this.currentResults ? this.currentResults.length : 0;
        this.modalCount.textContent = 'Show ' + n + (n === 1 ? ' result' : ' results');
      }

      if (this.appliedEl) {
        var chips = [];
        function applied(group, value, label) {
          chips.push('<button type="button" class="gg-chip is-active" data-gg-chip'
            + ' data-group="' + group + '" data-value="' + escapeHtml(value) + '"'
            + ' aria-label="Remove filter: ' + escapeHtml(label) + '">'
            + escapeHtml(label) + ' <span aria-hidden="true">✕</span></button>');
        }
        if (st.collection) applied('collection', st.collection.handle, st.collection.label);
        st.supp.forEach(function (v) { applied('supp', v, v); });
        st.merch.forEach(function (v) { applied('merch', v, v); });
        st.avail.forEach(function (v) { applied('avail', v, v); });
        st.flavor.forEach(function (v) { applied('flavor', v, titleCase(v)); });
        if (st.priceMin != null || st.priceMax != null) {
          var priceLabel = st.priceMin != null && st.priceMax != null
            ? st.priceMin + '–' + st.priceMax
            : st.priceMin != null ? 'Over ' + st.priceMin : 'Under ' + st.priceMax;
          applied('price', 'price', priceLabel);
        }
        if (chips.length > 1) {
          chips.push('<button type="button" class="gg-chip gg-chip--clear" data-gg-clear-filters>'
            + (self.strings.clearFilters || 'Clear filters') + '</button>');
        }
        this.appliedEl.innerHTML = chips.join('');
        this.appliedEl.hidden = !chips.length;
      }
    }

    /* iOS ignores overflow:hidden on body for touch scrolling — the page
       behind a sheet still pans and rubber-bands. Fixing the body in place
       is the reliable lock; scroll position is restored on unlock. */
    lockScroll() {
      if (this._scrollLocked) return;
      this._scrollLocked = true;
      this._scrollY = window.scrollY || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = -this._scrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (!this._scrollLocked) return;
      this._scrollLocked = false;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this._scrollY || 0);
    }

    openFilters() {
      if (!this.modal) return;
      this._modalOpen = true;
      this.modal.hidden = false;
      var modal = this.modal;
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
      this.lockScroll();
      document.body.classList.add('gg-filters-open');
      var closeBtn = this.modal.querySelector('.gg-filters-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    closeFilters() {
      if (!this.modal || !this._modalOpen) return;
      this._modalOpen = false;
      var self = this;
      this.modal.classList.remove('is-open');
      this.unlockScroll();
      document.body.classList.remove('gg-filters-open');
      setTimeout(function () {
        if (!self._modalOpen && self.modal) self.modal.hidden = true;
      }, 260);
      if (this.filtersBtn) this.filtersBtn.focus();
    }

    showError() {
      if (this.resultsEl) this.resultsEl.innerHTML = '';
      if (this.errorEl) this.errorEl.hidden = false;
    }

    updateUrl(q) {
      try {
        var st = this.state;
        var url = new URL(window.location.href);
        var sp = url.searchParams;
        function put(key, val) { if (val) sp.set(key, val); else sp.delete(key); }
        put('q', q);
        put('sort', this._sortExplicit ? st.sort : '');
        put('supp', Array.from(st.supp).join(','));
        put('merch', Array.from(st.merch).join(','));
        put('avail', Array.from(st.avail).join(','));
        put('flavor', Array.from(st.flavor).join(','));
        put('pmin', st.priceMin != null ? String(st.priceMin) : '');
        put('pmax', st.priceMax != null ? String(st.priceMax) : '');
        put('col', st.collection ? st.collection.handle : (this._pendingCollection || ''));
        history.replaceState(history.state, '', url.toString());
      } catch (e) { /* noop */ }
    }

    /* ---------- events ---------- */

    /* All listeners are delegated to the host element, which survives section
       re-renders — never bind to child nodes directly, they get swapped out. */
    bindEvents() {
      // Element-bound listeners survive detach/reattach — never double-bind.
      if (this._eventsBound) return;
      this._eventsBound = true;

      var self = this;
      var qDebounce;
      var priceDebounce;

      this.addEventListener('submit', function (e) {
        if (!e.target.closest('[data-gg-form]')) return;
        e.preventDefault();
        self.state.q = self.input ? self.input.value : '';
        self.state.visible = self.perPage;
        self.run();
      });

      this.addEventListener('input', function (e) {
        var t = e.target;
        if (t.hasAttribute('data-gg-input')) {
          if (self.clearBtn) self.clearBtn.hidden = !t.value;
          clearTimeout(qDebounce);
          qDebounce = setTimeout(function () {
            self.state.q = t.value;
            self.state.visible = self.perPage;
            self.run();
          }, 140);
        } else if (t.hasAttribute('data-gg-price-min') || t.hasAttribute('data-gg-price-max')) {
          // Negative prices don't exist — strip the sign as it's typed
          if (t.value.indexOf('-') !== -1) t.value = t.value.replace(/-/g, '');
          clearTimeout(priceDebounce);
          priceDebounce = setTimeout(function () {
            var min = parseFloat(self.priceMinEl && self.priceMinEl.value);
            var max = parseFloat(self.priceMaxEl && self.priceMaxEl.value);
            self.state.priceMin = isNaN(min) ? null : Math.max(0, min);
            self.state.priceMax = isNaN(max) ? null : Math.max(0, max);
            self.state.visible = self.perPage;
            self.run();
          }, 250);
        }
      });

      this.addEventListener('change', function (e) {
        if (!e.target.hasAttribute('data-gg-sort')) return;
        self.state.sort = e.target.value;
        self._sortExplicit = true;
        self.run();
      });

      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self._qvOpen) {
          self.closeQuickView();
          return;
        }
        if (e.key === 'Escape' && self._modalOpen) {
          self.closeFilters();
          return;
        }
        // type="number" still accepts these characters — block them at the source
        if ((e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E')
          && e.target.hasAttribute
          && (e.target.hasAttribute('data-gg-price-min') || e.target.hasAttribute('data-gg-price-max'))) {
          e.preventDefault();
        }
      });

      this.addEventListener('click', function (e) {
        // Featured tabs act as quick filters; modifier clicks still navigate.
        var tile = e.target.closest('[data-gg-featured-collection]');
        if (tile && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
          e.preventDefault();
          var handle = tile.getAttribute('data-gg-featured-collection');
          if (self.state.collection && self.state.collection.handle === handle) {
            self.state.collection = null;
            self.state.visible = self.perPage;
            self.run();
            return;
          }
          var label = tile.getAttribute('data-gg-featured-label') || handle;
          tile.classList.add('is-loading');
          self.fetchCollectionIds(handle).then(function (ids) {
            tile.classList.remove('is-loading');
            self.state.collection = { handle: handle, label: label, ids: ids };
            self.state.visible = self.perPage;
            self.run();
          }).catch(function () {
            tile.classList.remove('is-loading');
            window.location.href = tile.href; // fallback: behave like a normal link
          });
          return;
        }

        var arrow = e.target.closest('[data-gg-rail-prev], [data-gg-rail-next]');
        if (arrow) {
          var arrowRail = arrow.parentElement.querySelector('[data-gg-rail]');
          if (arrowRail) {
            var dir = arrow.hasAttribute('data-gg-rail-prev') ? -1 : 1;
            arrowRail.scrollBy({ left: dir * Math.max(arrowRail.clientWidth * 0.8, 160), behavior: 'smooth' });
          }
          return;
        }
        if (e.target.closest('[data-gg-clear]')) {
          if (self.input) {
            self.input.value = '';
            self.input.focus();
          }
          if (self.clearBtn) self.clearBtn.hidden = true;
          self.state.q = '';
          self.state.visible = self.perPage;
          self.run();
          return;
        }
        if (e.target.closest('[data-gg-clear-filters]')) {
          self.state.supp.clear();
          self.state.merch.clear();
          self.state.avail.clear();
          self.state.flavor.clear();
          self.state.collection = null;
          self.state.priceMin = self.state.priceMax = null;
          if (self.priceMinEl) self.priceMinEl.value = '';
          if (self.priceMaxEl) self.priceMaxEl.value = '';
          self.state.visible = self.perPage;
          self.run();
          return;
        }
        var qvTrigger = e.target.closest('[data-gg-qv]');
        if (qvTrigger && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
          e.preventDefault();
          self.openQuickView(qvTrigger.getAttribute('data-handle'), qvTrigger.getAttribute('href'));
          return;
        }
        if (e.target.closest('[data-gg-qv-dismiss]')) {
          self.closeQuickView();
          return;
        }
        var qvOpt = e.target.closest('[data-gg-qv-opt]');
        if (qvOpt) {
          var oi = parseInt(qvOpt.getAttribute('data-oi'), 10);
          self._qvSelection[oi] = qvOpt.getAttribute('data-value');
          self.syncQuickViewVariant();
          return;
        }
        if (e.target.closest('[data-gg-qv-modal] [data-atc-trigger]')) {
          // Theme JS performs the add; the cart drawer takes over from here
          setTimeout(function () { self.closeQuickView(); }, 900);
          return;
        }
        if (e.target.closest('[data-gg-filters-open]')) {
          self.openFilters();
          return;
        }
        if (e.target.closest('[data-gg-filters-dismiss]') || e.target.closest('[data-gg-filters-backdrop]')) {
          self.closeFilters();
          return;
        }
        var chip = e.target.closest('[data-gg-chip]');
        if (chip) {
          var group = chip.getAttribute('data-group');
          var value = chip.getAttribute('data-value');
          if (group === 'price') {
            // The applied price chip clears the whole range
            self.state.priceMin = self.state.priceMax = null;
            if (self.priceMinEl) self.priceMinEl.value = '';
            if (self.priceMaxEl) self.priceMaxEl.value = '';
          } else if (group === 'collection') {
            self.state.collection = null;
          } else {
            var set = group === 'supp' ? self.state.supp
              : group === 'merch' ? self.state.merch
              : group === 'flavor' ? self.state.flavor
              : self.state.avail;
            if (set.has(value)) set.delete(value); else set.add(value);
          }
          self.state.visible = self.perPage;
          self.run();
          return;
        }
        if (e.target.closest('[data-gg-more]')) self.expand();
      });

      // Hide any image that fails to load instead of showing a broken icon.
      this.addEventListener('error', function (e) {
        if (e.target && e.target.tagName === 'IMG') e.target.style.display = 'none';
      }, true);

      // Desktop mouse users can drag the chip rails; the click that ends a
      // drag is suppressed so it doesn't follow a chip link. Touch scrolling
      // is native overflow behavior — the drag handler ignores touch.
      var dragRail = null;
      var dragStartX = 0;
      var dragStartScroll = 0;
      var dragMoved = false;
      this.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        var rail = e.target.closest('[data-gg-rail]');
        if (!rail || rail.scrollWidth <= rail.clientWidth) return;
        dragRail = rail;
        dragStartX = e.clientX;
        dragStartScroll = rail.scrollLeft;
        dragMoved = false;
      });
      this.addEventListener('pointermove', function (e) {
        if (!dragRail) return;
        var dx = e.clientX - dragStartX;
        if (!dragMoved && Math.abs(dx) > 6) dragMoved = true;
        if (dragMoved) dragRail.scrollLeft = dragStartScroll - dx;
      });
      var endDrag = function () {
        if (dragRail && dragMoved) {
          self._railDragged = true;
          setTimeout(function () { self._railDragged = false; }, 0);
        }
        dragRail = null;
      };
      this.addEventListener('pointerup', endDrag);
      this.addEventListener('pointercancel', endDrag);
      this.addEventListener('click', function (e) {
        if (self._railDragged && e.target.closest('[data-gg-rail]')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Keep the trailing fade and arrow states in sync while a rail scrolls.
      this.addEventListener('scroll', function (e) {
        var rail = e.target;
        if (rail && rail.hasAttribute && rail.hasAttribute('data-gg-rail')) {
          self.syncRailState(rail);
        }
      }, true);

      if (this.clearBtn && this.input) this.clearBtn.hidden = !this.input.value;
    }
  }

  customElements.define('gg-instant-search', InstantSearchElement);
})();
